import { type ReactNode, useCallback, useMemo } from "react";

import useAccount from "../../../hooks/useAccount";
import type { Subscription } from "../../../hooks/useAccountTypes";
import {
  convertIndividualSubscriptionToLegacySubscription,
  useUpdateApplePaymentInfo,
  useUpdatePaddlePaymentInfo,
} from "../../../hooks/usePaddle";
import type { IndividualSubscription } from "../../../hooks/useTeamsTypes";
import { SubscriptionType } from "../../../types";
import { GracePeriodBanner } from "../../GracePeriodBanner/GracePeriodBanner";
import {
  GenericErrorModal,
  PaymentInfoUpdateFinishedModal,
  PaymentInfoUpdateSuccessModal,
} from "../../Modal/Modal";
import SubscriptionInfo from "../../SubscriptionInfo/SubscriptionInfo";

export const IndividualSubscriptionInfo: React.FC<{
  subscription: IndividualSubscription;
  subscriptionCTA?: ReactNode;
}> = ({ subscription, subscriptionCTA }) => {
  const isInGracePeriod = Boolean(subscription.gracePeriodExpirationDate);

  const { data: accountData } = useAccount();

  const legacySubscription: Subscription = useMemo(
    () => convertIndividualSubscriptionToLegacySubscription(subscription),
    [subscription]
  );

  const {
    mutate: mutateUpdateApplePaymentInfo,
    isPending: isUpdateApplePaymentInfoPending,
    isSuccess: isUpdateApplePaymentInfoSuccess,
    isError: isUpdateApplePaymentInfoError,
    reset: resetUpdateApplePaymentInfo,
  } = useUpdateApplePaymentInfo();
  const {
    mutate: mutateUpdatePaddlePaymentInfo,
    isPending: isUpdatePaddlePaymentInfoPending,
    isSuccess: isUpdatePaddlePaymentInfoSuccess,
    isError: isUpdatePaddlePaymentInfoError,
    reset: resetUpdatePaddlePaymentInfo,
  } = useUpdatePaddlePaymentInfo();

  const paddlePaymentUpdateRefreshTimedOut =
    subscription.type === SubscriptionType.Paddle &&
    isUpdatePaddlePaymentInfoSuccess &&
    isInGracePeriod;
  const appStorePaymentUpdateFinished =
    subscription.type === SubscriptionType.AppStore &&
    isUpdateApplePaymentInfoSuccess &&
    isInGracePeriod;
  const paddlePaymentUpdateFinished =
    subscription.type === SubscriptionType.Paddle &&
    isUpdatePaddlePaymentInfoSuccess &&
    !isInGracePeriod;

  const handlePaymentInfoUpdate = useCallback(
    (subscription: Subscription) => {
      if (subscription.type === SubscriptionType.Paddle) {
        return mutateUpdatePaddlePaymentInfo(legacySubscription);
      }

      if (subscription.type === SubscriptionType.AppStore) {
        return mutateUpdateApplePaymentInfo();
      }
    },
    [mutateUpdateApplePaymentInfo, mutateUpdatePaddlePaymentInfo, legacySubscription]
  );

  const handleDialogClose = useCallback(() => {
    resetUpdateApplePaymentInfo();
    resetUpdatePaddlePaymentInfo();
  }, [resetUpdateApplePaymentInfo, resetUpdatePaddlePaymentInfo]);

  if (!accountData) {
    return null;
  }

  return (
    <>
      {isInGracePeriod && (
        <GracePeriodBanner
          paymentInfoUpdate={handlePaymentInfoUpdate}
          paymentInfoUpdatePending={
            isUpdateApplePaymentInfoPending || isUpdatePaddlePaymentInfoPending
          }
        />
      )}
      <SubscriptionInfo
        hasAppStoreSubscription={accountData.hasAppStoreSubscription}
        subscription={legacySubscription}
        techTrialAvailable={accountData?.techTrialAvailable}
        handlePaymentInfoUpdate={handlePaymentInfoUpdate}
        subscriptionCTA={subscriptionCTA}
      />
      <PaymentInfoUpdateFinishedModal
        open={paddlePaymentUpdateRefreshTimedOut || appStorePaymentUpdateFinished}
        onClose={handleDialogClose}
        telemetryName="individual_subscription_payment_update_finished"
      />
      <PaymentInfoUpdateSuccessModal
        open={paddlePaymentUpdateFinished}
        onClose={handleDialogClose}
        telemetryName="individual_subscription_payment_update_success"
      />
      <GenericErrorModal
        open={isUpdatePaddlePaymentInfoError || isUpdateApplePaymentInfoError}
        onClose={handleDialogClose}
        telemetryName="individual_subscription_payment_update_error"
      />
    </>
  );
};
